import axios from "axios";
import { API } from "./apiUrls";
import { placementErrorMessage } from "./orderCreationError";

/*
 * Booking THROUGH a draft: update the draft with the desk's payload, then place it via the draft API,
 * so the draft becomes Placed with its own reference and can never be booked twice. The result is
 * translated into the create-order response dialect the desks already interpret, so the booked
 * ticket, the issue modal and the wallet refresh all work unchanged.
 *
 * Works against both OMS generations: with the placement revamp (OMS #248) the place response carries
 * `placed_order` (waybill, courier, label); without it we still get the shipment id and reference.
 */

const headersFor = (merchantId) => ({
  Authorization: `${localStorage.getItem("authToken")}`,
  "Content-Type": "application/json",
  merchantId,
});

const referenceOf = (draft) => draft?.order_data?.shipment_details?.order_details?.order_reference_number || null;

/** Draft place response → { metadata, result } as create-order would answer. */
export function placeResponseToOrderCreationDialect(draft) {
  const po = draft?.placed_order || null;
  const summary = draft?.placed_order_summary || null;
  const status = draft?.draft_status;

  if (status === "PLACED") {
    const waybill = po?.waybill || summary?.waybill || null;
    const processing = po?.state === "PROCESSING" || (!waybill && !summary);
    return {
      metadata: {
        success: true,
        status: processing ? 102 : 200,
        message: processing ? "Order accepted and is being processed" : "Order placed successfully",
        retryable: false,
      },
      result: {
        shipment_order_id: draft.placed_shipment_order_id || po?.shipment_order_id || null,
        order_reference_number: po?.order_reference_number || summary?.order_reference_number || referenceOf(draft),
        waybill,
        courier_name: po?.carrier_partner_public_name || summary?.carrier_partner_public_name || null,
        carrier_partner_id: po?.carrier_partner_id || summary?.carrier_partner_id || null,
        label_url: po?.label_url || null,
        commercial_invoice_url: po?.invoice_url || null,
        total_amount_charged_after_tax: po?.total_amount_charged_after_tax ?? null,
        currency_code: po?.currency_code || null,
        min_tat_in_days: po?.min_tat_in_days ?? null,
        max_tat_in_days: po?.max_tat_in_days ?? null,
        draft_order_id: draft.draft_order_id,
      },
    };
  }

  if (status === "FAILED") {
    const error = draft?.placement_error || {};
    return {
      metadata: {
        success: false,
        status: 422,
        message: placementErrorMessage(error.error_message),
        retryable: Boolean(error.retryable),
        error_code: error.error_code || null,
      },
      result: null,
    };
  }

  // QUEUED or anything else: the attempt is still in flight somewhere.
  return {
    metadata: { success: true, status: 102, message: "Order accepted and is being processed", retryable: false },
    result: { order_reference_number: referenceOf(draft), draft_order_id: draft?.draft_order_id || null },
  };
}

/**
 * Update-then-place. Resolves to an axios-shaped `{ data }` in the create-order dialect; rejects only
 * when the network itself fails, so callers keep their existing catch path.
 */
export async function bookThroughDraft(draftOrderId, orderData) {
  const merchantId = localStorage.getItem("merchantId");
  const headers = headersFor(merchantId);

  try {
    await axios.put(API.updateDraftOrder(draftOrderId), { order_data: orderData, order_source: "UI_PANEL" }, { headers });
  } catch (error) {
    const status = error?.response?.status;
    if (status === 422) {
      return { data: { metadata: { success: false, status: 422, retryable: false, message: "This draft was already booked or discarded. Open Draft Orders to see its current state." } } };
    }
    if (status === 409) {
      return { data: { metadata: { success: false, status: 409, retryable: false, message: "This order reference is already used by another draft. Change the reference or place that draft instead." } } };
    }
    throw error;
  }

  try {
    const response = await axios.post(API.placeDraftOrder(draftOrderId), {}, { headers });
    return { data: placeResponseToOrderCreationDialect(response.data) };
  } catch (error) {
    const status = error?.response?.status;
    if (status === 409) {
      return { data: { metadata: { success: false, status: 409, retryable: false, message: "This draft is already being booked. Check Draft Orders in a moment instead of booking it again." } } };
    }
    if (status === 422) {
      return { data: { metadata: { success: false, status: 422, retryable: false, message: "This draft can no longer be booked. Open Draft Orders to see its current state." } } };
    }
    throw error;
  }
}
